import React, {useCallback, useRef, useState} from 'react';
import TableRow from "./TableRow";
import JsonScreenHoc from "../../../../../HocHelpers/JsonScreenHoc";
import TableBodyRendering from "./TableBodyRendering";
import styled from "styled-components";
import useShallowCompareEffect from "../../../../../Hooks/useShallowCompareEffect";
import {useDeepCompareCallback} from "use-deep-compare";

const TableStyles = styled.table((props) => ({
    borderCollapse: 'separate',
    borderSpacing: 0,
    tableLayout: 'fixed',
    marginLeft: props.allowActions ? '-20px' : 0,
    width: '100%',
    'tr td': {
        'border-right': `1px solid ${props.styled.borderColor}`,
        'border-bottom': `1px solid ${props.styled.borderColor}`,
    },
    'tr th': {
        'border-top': `1px solid ${props.styled.borderColor}`,
        'border-right': `1px solid ${props.styled.borderColor}`,
        'border-bottom': `1px solid ${props.styled.borderColor}`,
    },
    'tr:first-child td': {
        'border-top': props.hideHeader ? `1px solid ${props.styled.borderColor}` : '',
    },
    'tr th:nth-child(2)': {
        'border-left': `${props.allowActions ? "1px" : "0"} solid ${props.styled.borderColor}`
    },
    'tr td:nth-child(2)': {
        'border-left': `${props.allowActions ? "1px" : "0"} solid ${props.styled.borderColor}`
    },
    'tr th:nth-child(1)': {
        'border-left': `${!props.allowActions ? "1px" : "0"} solid ${props.styled.borderColor}`
    },
    'tr td:nth-child(1)': {
        'border-left': `${!props.allowActions ? "1px" : "0"} solid ${props.styled.borderColor}`
    },
    'tr:first-child th:nth-child(1)': {
        'border-top-left-radius': (!props.allowActions && props.styled.borderRadius || 0) + 'px'
    },
    'tr:first-child th:nth-child(2)': {
        'border-top-left-radius': (props.allowActions && props.styled.borderRadius || 0) + 'px'
    },
    'tr:first-child th:last-child': {
        'border-top-right-radius': (props.styled.borderRadius || 0) + 'px'
    },
    'tr:last-child td:nth-child(2)': {
        'border-bottom-left-radius': (props.allowActions && props.styled.borderRadius || 0) + 'px'
    },
    'tr:last-child td:nth-child(1)': {
        'border-bottom-left-radius': (!props.allowActions && props.styled.borderRadius || 0) + 'px'
    },
    'tr:last-child td:last-child': {
        'border-bottom-right-radius': (props.styled.borderRadius || 0) + 'px'
    }
}));
const TableRenderingComponent = ({component, actionsContext, containerArrayPath, ...props}) => {
    const {
        properties: {path, allowActions, paged, pageSize, hideHeader} = {},
        events,
        styles: sourceStyles,
        children = [],
    } = component;
    const {doActions} = props;
    const styles = sourceStyles[0] || {};
    const [onClickEvent, setClickEvent] = React.useState(false);
    const [hoverInfo, setHoverInfo] = useState(null); // {rowIndex, y}
    const tableRef = useRef();

    const handleMouseMove = useDeepCompareCallback((e, rowIndex) => {
        if (!e || !allowActions) {
            setHoverInfo(null)
            return
        }
        const rect = e.currentTarget.getBoundingClientRect();
        const offsetY = e.clientY - rect.top;
        // detect if near top or bottom edge (5px tolerance)
        if (offsetY < 5) {
            setHoverInfo({rowIndex, y: rect.top});
        } else if (rect.height - offsetY < 5) {
            setHoverInfo({rowIndex: rowIndex + 1, y: rect.bottom - 5});
        } else {
            setHoverInfo(null);
        }
    }, []);

    const handleMouseLeave = useDeepCompareCallback(() => {
        setHoverInfo(null)
    }, [])

    useShallowCompareEffect(() => {
        if (events && events.length) {
            let clickEvent = events.find(event => event.event === 'onClick');

            if (clickEvent) {
                setClickEvent(clickEvent.actions)
            }
        }
    }, [events]);

    const rowClickHandler = useCallback((context) => {
        doActions(onClickEvent, context)
    }, [JSON.stringify(onClickEvent)])

    const insertRow = useDeepCompareCallback((index) => {
        props.addGroup(path, index);
    }, [props.addGroup])

    return (
        <div style={{position: "relative"}} ref={tableRef}>
            <TableStyles
                styled={{
                    borderRadius: styles.borderRadius,
                    borderColor: styles.borderColor,
                }}
                hideHeader={hideHeader}
                allowActions={allowActions}
            >
                {path ? (
                    <>
                        {!hideHeader && (
                            <TableRow
                                component={children[0]}
                                rowIndex={0}
                                actionsContext={actionsContext}
                                containerArrayPath={containerArrayPath}
                                allowActions={allowActions}
                            />
                        )}
                        <TableBodyRendering
                            component={children[1]}
                            actionsContext={actionsContext}
                            path={path}
                            allowActions={allowActions}
                            paged={paged}
                            pageSize={pageSize}
                            onClickEvent={rowClickHandler}
                            handleMouseMove={handleMouseMove}
                            handleMouseLeave={handleMouseLeave}
                        />
                    </>
                ) : (
                    children.map((tr, index) => {
                        return (
                            <TableRow
                                key={index}
                                component={tr}
                                rowIndex={index}
                                actionsContext={actionsContext}
                                containerArrayPath={containerArrayPath}
                            />
                        )
                    })
                )}
            </TableStyles>
            {hoverInfo && (
                <div
                    onMouseEnter={() => setHoverInfo(hoverInfo)} // ✅ keep visible when hovering button
                    onMouseLeave={() => setHoverInfo(null)}
                    onClick={() => insertRow(hoverInfo.rowIndex)}
                    style={{
                        position: "absolute",
                        width: 'calc(100% - 20px)',
                        left: "0",
                        top: hoverInfo.y - tableRef.current.getBoundingClientRect().top,
                        zIndex: 10,
                        background: "transparent",
                        padding: "3px 5px",
                        cursor: "cell"
                    }}
                >
                </div>)}
        </div>
    )
}

export default JsonScreenHoc(TableRenderingComponent)
